(function (root, factory) {
  if (typeof define === 'function' && define.amd) {
    define(['jquery', 'underscore', 'torso/modules/View', 'templates'], factory);

  } else if (typeof exports === 'object') {
    // CommonJS-style exports
    var templates = {
      paginationViewTemplate: require('./paginationViewTemplate.hbs')
    };
    module.exports = factory(require('jquery'), require('underscore'), require('backbone-torso/modules/View'), templates);
  } else {
    throw 'Only AMD and CommonJS imports are supported.';
  }
}(this, function($, _, TorsoView, templates) {
  'use strict';

  var NUM_PAGES_TO_SHOW = 5;
  var ELIPSIS_PAGE_OBJECT = {isElipsis: true}; //to render an elipsis in the template

  /**
   * This view renders a pagination list.
   * The view is 1-indexed
   * @class TorsoPaginationView
   * @constructor
   * @extends Torso.View
   */
  return TorsoView.extend({
    className: 'pagination',
    template: templates.paginationViewTemplate,
    events: {
      'click a': '_selectPage'
    },

    /**
     * @method initialize
     * @override
     */
    initialize: function() {
      this.listenTo(this.viewState, 'change', this.render);
    },

    /**
     * Updates the state of the view based on passed in args.
     * @method updateState
     * @param args {Object} Data to update the view's state with.
     *   @param args.fullListSize {Number} the size of the complete list of elements
     *   @param args.pageNumber {Number} the current page number
     *   @param args.pageSize {Number} how many elements to display per page
     */
    updateState: function(args) {
      var updatedProperties = _.pick(args, 'fullListSize', 'pageNumber', 'pageSize');
      this.set(updatedProperties);
    },

    /**
     * @method prepare
     * @override
     */
    prepare: function() {
      var pageNumberDoesNotExist = !this.viewState.has('pageNumber');
      var pageSizeDoesNotExist = !this.viewState.has('pageSize');
      if (pageNumberDoesNotExist || pageSizeDoesNotExist) {
        return {};
      }
      return {
        nextPage: this._getNextPage(),
        previousPage: this._getPreviousPage(),
        baseUrl: this._getRootHashWithoutPage(),
        pageList: this._generatePageList()
      };
    },

    /**
     * This method returns the next page number
     * @private
     * @method  getNextPage
     * @return {Integer} the next page number or null
     */
    _getNextPage: function(){
      return (this.get('pageNumber') < this._getLastPage()) ? this.get('pageNumber') + 1 : null;
    },

    /**
     * This method returns the previous page number
     * @private
     * @method  getPreviousPage
     * @return {Integer} The previous page number or null
     */
    _getPreviousPage: function(){
      return (this.get('pageNumber') > 1) ? this.get('pageNumber') - 1 : null;
    },

    /**
     * Returns the root url without the page query parameter but it will keep other query parameters.
     * @private
     * @method  _getRootHashWithoutPage
     * @return {String}            The current url stripped of GET variables
     * @example
     *   '#bills/page/1' returns '#bills'
     * @example
     *   '#bills/page/1/list' returns '#bills/list'
     */
    _getRootHashWithoutPage: function() {
      var pageParamRegex = /\/page\/-*\d*/gi;
      var currLocation = window.location.href;
      var newUrl = currLocation.replace(pageParamRegex, '');
      if (newUrl.indexOf('#') === -1) {
        // add # to the end if this page is the default perspective (i.e. the dashboard at /patient-portal/patient.html)
        newUrl += '#';
      }
      return newUrl;
    },

    /**
     * This method will generate an array of objects for the template to render.
     * @private
     * @method generatePageList
     * @return {Array}           An array of objects to be displayed on the template for pagination links
     */
    _generatePageList: function() {
      var range;
      var pageList = [];
      var start = this._getStartOfRange();
      var lastPage = this._getLastPage();
      var end = this._getEndOfRange();
      var showLastPage = lastPage > 1;
      var showFirstPage = start > 1;
      var showLeftElipsis = start > 2;
      var showRightElipsis = start + NUM_PAGES_TO_SHOW < lastPage;

      if(showFirstPage){
        pageList.push(this._makePageObject(1));
      }

      if(showLeftElipsis) {
        pageList.push(ELIPSIS_PAGE_OBJECT);
      }

      range = _.range(start, end);
      _.each(range, function(pageNumber){
        var pageObject = this._makePageObject(pageNumber);
        pageList.push(pageObject);
      }, this);

      if(showRightElipsis) {
        pageList.push(ELIPSIS_PAGE_OBJECT);
      }

      if(showLastPage){
        pageList.push(this._makePageObject(lastPage));
      }

      return pageList;
    },


    /**
     * Returns the starting index for the page range
     * @private
     * @method  getStartPage
     */
    _getStartOfRange: function() {
      var lastPage = this._getLastPage();
      var lowerBound = Math.max(1, this.get('pageNumber') - NUM_PAGES_TO_SHOW / 2); //Used for most pages
      var upperBound = Math.max(1, (lastPage - NUM_PAGES_TO_SHOW) + 1); //Used for pages close to the last page
      return Math.ceil(Math.min(lowerBound, upperBound));
    },

    /**
     * Returns the last index for the page range
     * @private
     * @method  getStartPage
     */
    _getEndOfRange: function() {
      return Math.min(this._getStartOfRange() + NUM_PAGES_TO_SHOW, this._getLastPage());
    },

    /**
     * The last page based on the fullListSize and the pageSize
     * @private
     * @method  getLastPage
     * @return {Number} the last page
     */
    _getLastPage: function(){
      return Math.ceil(this.get('fullListSize') / this.get('pageSize'));
    },

    /**
     * Makes a page object for the pageList
     * @private
     * @method  makePageObject
     * @param  {Number}  pageNumber    The page number of the page
     * @return {Object}                Example:  {pageNumber: 1, baseUrl: string}
     *                                 Example 2: {pageNumber: 1, baseUrl: string, isCurrentPage: true}
     */
    _makePageObject: function(pageNumber){
      return {
        pageNumber: pageNumber,
        baseUrl: this._getRootHashWithoutPage(),
        isCurrentPage: pageNumber === this.get('pageNumber')
      };
    },

    /**
     * Updates this widget with the newly selected page.
     * @method _selectPage
     * @private
     * @param event {Event} the event that triggered this call.
     *   @param [event.target] {Element} the target of the event that triggered this call.
     */
    _selectPage: function(event) {
      var clickedLink = $(event.target);
      var newPage = parseInt(clickedLink.attr('page'), 10);

      if (newPage) {
        // This is odd, but we want to prevent the default navigation and instead navigate before updating the view.
        // This is a ui optimization that made it feel cleaner when it rendered.
        event.preventDefault();
        this._redirectToPage(clickedLink);
        this.updateState({ pageNumber: newPage });
      }
    },

    /**
     * Extension point for views that want to handle pagination differently.
     *
     * @method _redirectToPage
     * @param clickedLink {jQuery.Selector} the link that was clicked wrapped using jQuery.
     * @private
     */
    _redirectToPage: function(clickedLink) {
      window.location = clickedLink.attr('href');
    }
  });
}));
